import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { Participant, Room } from 'livekit-client';
import { Icon } from '@web-chat-core/ui-kit';
import { useParticipant } from 'core';
import { selectRoom } from 'state/selectors';
import { modalStore } from 'state/store';
import {
    isLocalUserModerator,
    isMainModeratorParticipant,
    isModeratorParticipant,
} from 'useCase/moderation/status/moderators';
import { isParticipantSpeaker, isWebinarEnabled } from 'useCase/webinar/webinarStatus';
import { isWebinarFeatureOn } from 'useCase/featureFlags/featureFlagsApi';
import { useSIPParticipant } from 'useCase/SIPTelephony/useSIPParticipant';
import { ModalName } from 'features/Modals/ModalProvider';
import type { ModerationConfirmState, ModerationMenuItemModel } from '../types';
import { useModerationActions } from './useModerationActions';

export type UseModerationMenuItemsResult = {
    isCanShowMenu: boolean;
    menuItems: ModerationMenuItemModel[];
    room: Room | null;
};

/**
 * Хук меню модерации: проверка прав показа, сбор данных об участнике и построении списка меню
 */
export const useModerationMenuItems = (participant: Participant): UseModerationMenuItemsResult => {
    const { t } = useTranslation();
    const { checkIsSIPParticipant } = useSIPParticipant();
    const { isAudioMuted, cameraPublication } = useParticipant(participant);
    const room = selectRoom();
    const setConfirm = (data: ModerationConfirmState) => modalStore.openModal(ModalName.MODERATION_CONFIRM, data);

    const isModerator = isLocalUserModerator();
    const localParticipant = room?.localParticipant;
    const isLocalMainModerator = localParticipant ? isMainModeratorParticipant(localParticipant) : false;
    const isTargetLocal = participant.isLocal;
    const isTargetSIP = checkIsSIPParticipant(participant);
    const isTargetModerator = isModeratorParticipant(participant);
    const isTargetMainModerator = isMainModeratorParticipant(participant);
    const isTargetMicOn = !isAudioMuted;
    const isTargetCameraOn = cameraPublication?.isMuted === false;
    const isCanShowMenu = isModerator && !isTargetSIP;
    const isWebinarMode = isWebinarFeatureOn() && isWebinarEnabled();
    const isTargetSpeaker = isParticipantSpeaker(participant);

    const {
        muteMic,
        muteCam,
        inviteToSpeak,
        openMuteMicOthersConfirm,
        openMuteCamOthersConfirm,
        openKickConfirm,
        openMuteOthersFromLocalTileMicConfirm,
        openMuteOthersFromLocalTileCamConfirm,
        openAssignModeratorConfirm,
        revokeModerator,
        assignSpeaker,
        revokeSpeaker,
    } = useModerationActions({
        targetParticipant: participant,
        room,
        setConfirm,
    });

    const menuItems = useMemo<ModerationMenuItemModel[]>(() => {
        if (isTargetLocal) {
            return [
                {
                    id: 'mute-others-mic',
                    onClick: openMuteOthersFromLocalTileMicConfirm,
                    label: t('web_videoconference_moderator_mute_others_except'),
                    icon: <Icon icon="icMicrophoneOffAll24" size={24} colorVariant="bright" />,
                },
                {
                    id: 'mute-others-cam',
                    onClick: openMuteOthersFromLocalTileCamConfirm,
                    label: t('web_videoconference_moderator_stop_others_video_except'),
                    icon: <Icon icon="icStopVideoAll24" size={24} colorVariant="bright" />,
                },
            ];
        }

        const items: ModerationMenuItemModel[] = [];

        // Назначение/снятие спикера — всегда первым в режиме вебинара
        if (isWebinarMode && !isTargetModerator) {
            if (!isTargetSpeaker) {
                items.push({
                    id: 'assign-speaker',
                    onClick: assignSpeaker,
                    label: t('vks_webinar_assign_speaker'),
                    icon: <Icon icon="icSpeaker" size={24} colorVariant="bright" />,
                });
            } else {
                items.push({
                    id: 'revoke-speaker',
                    onClick: revokeSpeaker,
                    label: t('vks_webinar_revoke_speaker'),
                    icon: <Icon icon="icSpeaker" size={24} colorVariant="bright" />,
                });
            }
        }

        // В режиме вебинара: мик/попросить высказаться только для спикеров
        const canControlMic = !isWebinarMode || isTargetSpeaker;
        if (canControlMic) {
            if (isTargetMicOn) {
                items.push({
                    id: 'mute-mic',
                    onClick: muteMic,
                    label: t('web_videoconference_moderator_mute_microphone'),
                    icon: <Icon icon="icMicrophoneOff" size={24} colorVariant="bright" />,
                });
            } else {
                items.push({
                    id: 'request-to-speak',
                    onClick: inviteToSpeak,
                    label: t('moderator_request_to_speak'),
                    icon: <Icon icon="icMicrophoneOn" size={24} colorVariant="bright" />,
                });
            }
        }

        items.push({
            id: 'mute-others-mic',
            onClick: openMuteMicOthersConfirm,
            label: t('web_videoconference_moderator_mute_others_except'),
            icon: <Icon icon="icMicrophoneOffAll24" size={24} colorVariant="bright" />,
        });

        if (isTargetCameraOn) {
            items.push({
                id: 'mute-cam',
                onClick: muteCam,
                label: t('web_videoconference_moderator_stop_video'),
                icon: <Icon icon="icStopVideo24" size={24} colorVariant="bright" />,
            });
        }

        items.push({
            id: 'mute-others-cam',
            onClick: openMuteCamOthersConfirm,
            label: t('web_videoconference_moderator_stop_others_video_except'),
            icon: <Icon icon="icStopVideoAll24" size={24} colorVariant="bright" />,
        });

        if (!isTargetMainModerator && !isTargetModerator) {
            items.push({
                id: 'assign-moderator',
                onClick: openAssignModeratorConfirm,
                label: t('web_videoconference_moderator_make_moderator'),
                icon: <Icon icon="icModerator24" size={24} colorVariant="bright" />,
            });
        }

        if (isTargetModerator && !isTargetMainModerator) {
            items.push({
                id: 'revoke-moderator',
                onClick: revokeModerator,
                label: t('revoke_moderator_role'),
                icon: <Icon icon="icModerator24" size={24} colorVariant="bright" />,
            });
        }

        if (!isTargetMainModerator) {
            items.push({
                id: 'kick',
                onClick: openKickConfirm,
                label: t('web_videoconference_moderator_remove_from_meeting'),
                icon: <Icon icon="icExit" size={24} colorVariant="bright" />,
            });
        }

        return items;
    }, [
        isTargetLocal,
        isTargetMicOn,
        isTargetCameraOn,
        isLocalMainModerator,
        isTargetModerator,
        isTargetMainModerator,
        muteMic,
        muteCam,
        inviteToSpeak,
        openMuteMicOthersConfirm,
        openMuteCamOthersConfirm,
        openKickConfirm,
        openMuteOthersFromLocalTileMicConfirm,
        openMuteOthersFromLocalTileCamConfirm,
        openAssignModeratorConfirm,
        revokeModerator,
        isWebinarMode,
        isTargetSpeaker,
        assignSpeaker,
        revokeSpeaker,
        t,
    ]);

    return { isCanShowMenu, menuItems, room };
};
